import React, { useCallback, useRef, useEffect } from "react";
import { Label } from "../../ui/label";
import { Input } from "../../ui/input";
import { Button } from "../../ui/button";
import { Switch } from "../../ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import { RichTextEditor } from "../RichTextEditor";
import { AutomatedControlData } from "../types-automated";
import {
  OperatingEffectivenessStep,
  RollforwardInquiry,
  NATURE_OF_PROCEDURE_OPTIONS,
} from "../types";
import { Plus, Trash2, Copy } from "lucide-react";
import { ControlDocumentManager } from "../ControlDocumentManager";

interface RollforwardAutomatedProps {
  data: AutomatedControlData;
  onChange: (data: Partial<AutomatedControlData>) => void;
}

export const RollforwardAutomated: React.FC<RollforwardAutomatedProps> = ({ data, onChange }) => {
  // Use ref to access latest onChange without causing callback recreation
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // Stabilize callbacks for ControlDocumentManager to prevent infinite loops
  const handleRollforwardDocumentsChange = useCallback((documents: typeof data.rollforwardOperatingTestsDocuments) => {
    onChangeRef.current({ rollforwardOperatingTestsDocuments: documents });
  }, []);

  const handleRollforwardTextResponseChange = useCallback((textResponse: typeof data.rollforwardOperatingTestsTextResponse) => {
    onChangeRef.current({ rollforwardOperatingTestsTextResponse: textResponse });
  }, []);

  const addInquiry = useCallback(() => {
    const newInquiry: RollforwardInquiry = {
      id: `inquiry-${Date.now()}`,
      name: "",
      date: "",
    };
    onChangeRef.current({ rollforwardInquiries: [...data.rollforwardInquiries, newInquiry] });
  }, [data.rollforwardInquiries]);

  const removeInquiry = useCallback((id: string) => {
    onChangeRef.current({ rollforwardInquiries: data.rollforwardInquiries.filter((i) => i.id !== id) });
  }, [data.rollforwardInquiries]);

  const updateInquiry = useCallback((id: string, field: keyof RollforwardInquiry, value: string) => {
    onChangeRef.current({
      rollforwardInquiries: data.rollforwardInquiries.map((i) =>
        i.id === id ? { ...i, [field]: value } : i
      ),
    });
  }, [data.rollforwardInquiries]);

  const addRollforwardStep = useCallback(() => {
    const newStep: OperatingEffectivenessStep = {
      id: `rf-step-${Date.now()}`,
      stepDescription: "",
      natureOfProcedure: "",
      procedureDescription: "",
    };
    onChangeRef.current({ rollforwardSteps: [...data.rollforwardSteps, newStep] });
  }, [data.rollforwardSteps]);

  const removeRollforwardStep = useCallback((id: string) => {
    onChangeRef.current({ rollforwardSteps: data.rollforwardSteps.filter((s) => s.id !== id) });
  }, [data.rollforwardSteps]);

  const updateRollforwardStep = useCallback((id: string, field: keyof OperatingEffectivenessStep, value: string) => {
    onChangeRef.current({
      rollforwardSteps: data.rollforwardSteps.map((s) =>
        s.id === id ? { ...s, [field]: value } : s
      ),
    });
  }, [data.rollforwardSteps]);

  const copyInterimProcedures = useCallback(() => {
    const copiedSteps: OperatingEffectivenessStep[] = data.operatingProcedures.map((step, index) => ({
      id: `rf-copied-${Date.now()}-${index}`,
      stepDescription: step.stepDescription,
      natureOfProcedure: step.natureOfProcedure,
      procedureDescription: step.procedureDescription,
    }));
    onChangeRef.current({ rollforwardSteps: [...data.rollforwardSteps, ...copiedSteps] });
  }, [data.operatingProcedures, data.rollforwardSteps]);

  const handleIsInquirySufficientChange = useCallback((value: string) => {
    onChangeRef.current({ isInquirySufficient: value === "Yes" });
  }, []);

  const handleRollforwardOperatingEffectiveChange = useCallback((value: string) => {
    onChangeRef.current({ rollforwardOperatingEffective: value as "Yes" | "No" | "Not Yet Concluded" });
  }, []);

  return (
    <div className="bg-white/5 border border-white/10 rounded-lg">
      <div className="border-b border-white/10 p-6">
        <h2 className="text-xl font-semibold text-white">Rollforward</h2>
      </div>
      <div className="p-6 space-y-8">
        {/* Control Description Display */}
        {data.controlDescription && (
          <div className="p-4 border border-white/10 rounded-lg bg-black/20">
            <Label className="text-sm font-medium text-white mb-2 block">
              Control Description
            </Label>
            <div
              className="text-white/60"
              dangerouslySetInnerHTML={{ __html: data.controlDescription }}
            />
          </div>
        )}

        {/* Rollforward Period */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-white">What is the length of the rollforward period?</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-white">From Date</Label>
              <Input
                type="date"
                value={data.rollforwardFromDate}
                onChange={(e) => onChangeRef.current({ rollforwardFromDate: e.target.value })}
                className="bg-black/20 border-white/10 text-white"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-white">To Date</Label>
              <Input
                type="date"
                value={data.rollforwardToDate}
                onChange={(e) => onChangeRef.current({ rollforwardToDate: e.target.value })}
                className="bg-black/20 border-white/10 text-white"
              />
            </div>
          </div>
        </div>

        {/* Rollforward Inquiry */}
        <div className="space-y-6 border-t border-white/10 pt-6">
          <h3 className="text-lg font-medium text-white">Rollforward Inquiry</h3>

          {/* Who did we inquire with */}
          <div className="space-y-4">
            <h4 className="text-sm font-semibold text-white">
              i. Who did we inquire with to identify if significant changes to the control or process have occurred since interim testing?
            </h4>
            <Button
              variant="outline"
              onClick={addInquiry}
              className="border-white/10 text-white hover:bg-white/10"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Inquiry
            </Button>

            {data.rollforwardInquiries.length > 0 && (
              <div className="border border-white/10 rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-black/40">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-white/60">Name of respective SPOC</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-white/60">Date of Inquiry</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-white/60">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.rollforwardInquiries.map((inquiry) => (
                      <tr key={inquiry.id} className="border-t border-white/10">
                        <td className="px-4 py-3">
                          <Input
                            value={inquiry.name}
                            onChange={(e) => updateInquiry(inquiry.id, "name", e.target.value)}
                            className="bg-black/20 border-white/10 text-white"
                            placeholder="Enter name"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <Input
                            type="date"
                            value={inquiry.date}
                            onChange={(e) => updateInquiry(inquiry.id, "date", e.target.value)}
                            className="bg-black/20 border-white/10 text-white"
                          />
                        </td>
                        <td className="px-4 py-3 text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeInquiry(inquiry.id)}
                            className="text-red-400 hover:text-red-300"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* ICFR Changes */}
          <div className="space-y-4">
            <h4 className="text-sm font-semibold text-white">
              ii. Have there been any changes in the ICFR (internal control over financial reporting) that would impact the design and operating effectiveness of control?
            </h4>
            <Select
              value={data.hasIcfrChanges ? "Yes" : data.hasIcfrChanges === false ? "No" : ""}
              onValueChange={(value) => onChangeRef.current({ hasIcfrChanges: value === "Yes" })}
            >
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Select Yes/No" />
              </SelectTrigger>
              <SelectContent className="bg-black/90 border-white/10">
                <SelectItem value="Yes">Yes</SelectItem>
                <SelectItem value="No">No</SelectItem>
              </SelectContent>
            </Select>

            {data.hasIcfrChanges && (
              <div className="space-y-4 mt-4">
                <RichTextEditor
                  label="What were the changes?"
                  value={data.icfrChanges}
                  onChange={(content) => onChangeRef.current({ icfrChanges: content })}
                  placeholder="Enter changes..."
                />
                <RichTextEditor
                  label="Additional procedures performed"
                  value={data.additionalProceduresRollforward}
                  onChange={(content) => onChangeRef.current({ additionalProceduresRollforward: content })}
                  placeholder="Enter additional procedures..."
                />
              </div>
            )}
          </div>

          {/* GITC Operating Effectively */}
          <div className="space-y-4">
            <h4 className="text-sm font-semibold text-white">
              iii. Are the relevant general IT control(s) that the automated control is dependent upon operating effectively?
            </h4>
            <Select
              value={data.gitcOperatingEffectively ? "Yes" : data.gitcOperatingEffectively === false ? "No" : ""}
              onValueChange={(value) => onChangeRef.current({ gitcOperatingEffectively: value === "Yes" })}
            >
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Select Yes/No" />
              </SelectTrigger>
              <SelectContent className="bg-black/90 border-white/10">
                <SelectItem value="Yes">Yes</SelectItem>
                <SelectItem value="No">No</SelectItem>
              </SelectContent>
            </Select>

            {data.gitcOperatingEffectively !== undefined && (
              <RichTextEditor
                value={data.gitcDetails}
                onChange={(content) => onChangeRef.current({ gitcDetails: content })}
                placeholder="Enter details..."
              />
            )}
          </div>

          {/* Is inquiry & testing performed sufficient */}
          <div className="space-y-4">
            <h4 className="text-sm font-semibold text-white">
              iv. Is inquiry & testing performed sufficient for rollforward?
            </h4>
            <Select
              value={data.isInquirySufficient ? "Yes" : data.isInquirySufficient === false ? "No" : ""}
              onValueChange={handleIsInquirySufficientChange}
            >
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Select Yes/No" />
              </SelectTrigger>
              <SelectContent className="bg-black/90 border-white/10">
                <SelectItem value="Yes">Yes</SelectItem>
                <SelectItem value="No">No</SelectItem>
              </SelectContent>
            </Select>

            {data.isInquirySufficient === false && (
              <div className="space-y-4 mt-4">
                <div className="flex items-center justify-between">
                  <Label className="text-white">
                    Procedures performed to evaluate the operating effectiveness of control.
                  </Label>
                  <Button
                    variant="outline"
                    onClick={copyInterimProcedures}
                    className="border-white/10 text-white hover:bg-white/10"
                  >
                    <Copy className="h-4 w-4 mr-2" />
                    Copy Interim Procedures
                  </Button>
                </div>

                <Button
                  variant="outline"
                  onClick={addRollforwardStep}
                  className="border-white/10 text-white hover:bg-white/10"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Procedure
                </Button>

                {data.rollforwardSteps.length === 0 ? (
                  <div className="p-4 border border-white/10 rounded-lg bg-black/20 space-y-4">
                    <div className="space-y-2">
                      <Label className="text-white">Nature of testing procedure</Label>
                      <Select
                        value=""
                        onValueChange={(value) => {
                          const newStep: OperatingEffectivenessStep = {
                            id: `rf-default-${Date.now()}`,
                            stepDescription: "",
                            natureOfProcedure: value,
                            procedureDescription: "",
                          };
                          onChangeRef.current({ rollforwardSteps: [newStep] });
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select nature of procedure" />
                        </SelectTrigger>
                        <SelectContent className="bg-black/90 border-white/10">
                          {NATURE_OF_PROCEDURE_OPTIONS.map((option) => (
                            <SelectItem key={option} value={option}>
                              {option}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <RichTextEditor
                      label="Procedure Description"
                      value=""
                      onChange={(content) => {
                        const newStep: OperatingEffectivenessStep = {
                          id: `rf-default-${Date.now()}`,
                          stepDescription: "",
                          natureOfProcedure: "",
                          procedureDescription: content,
                        };
                        onChangeRef.current({ rollforwardSteps: [newStep] });
                      }}
                      placeholder="Describe the procedure..."
                    />
                  </div>
                ) : (
                  <div className="space-y-4">
                    {data.rollforwardSteps.map((step, index) => (
                      <div key={step.id} className="p-4 border border-white/10 rounded-lg bg-black/20 space-y-4">
                        <div className="flex items-center justify-between">
                          <h4 className="text-sm font-semibold text-white">
                            Procedure {index + 1}
                          </h4>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeRollforwardStep(step.id)}
                            className="text-red-400 hover:text-red-300"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>

                        <div className="space-y-2">
                          <Label className="text-white">Nature of testing procedure</Label>
                          <Select
                            value={step.natureOfProcedure || ""}
                            onValueChange={(value) => updateRollforwardStep(step.id, "natureOfProcedure", value)}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select nature of procedure" />
                            </SelectTrigger>
                            <SelectContent className="bg-black/90 border-white/10">
                              {NATURE_OF_PROCEDURE_OPTIONS.map((option) => (
                                <SelectItem key={option} value={option}>
                                  {option}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        <RichTextEditor
                          label="Procedure Description"
                          value={step.procedureDescription}
                          onChange={(content) => updateRollforwardStep(step.id, "procedureDescription", content)}
                          placeholder="Describe the procedure..."
                        />
                      </div>
                    ))}
                  </div>
                )}

                <div className="space-y-2">
                  <Label className="text-white">Perform tests of operating effectiveness</Label>
                  <p className="text-sm text-white/60">
                    For each activity, we document the procedures performed to test the operating effectiveness.
                  </p>
                  <ControlDocumentManager
                    controlId={data.controlId}
                    workspaceLinked={data.workspaceLinked}
                    documents={data.rollforwardOperatingTestsDocuments || []}
                    onDocumentsChange={handleRollforwardDocumentsChange}
                    textResponse={data.rollforwardOperatingTestsTextResponse}
                    onTextResponseChange={handleRollforwardTextResponseChange}
                    label="Rollforward Operating Tests"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Operating Effectiveness Testing Conclusion */}
          <div className="space-y-4 border-t border-white/10 pt-6">
            <h4 className="text-sm font-semibold text-white">
              v. Operating Effectiveness Testing Conclusion
            </h4>
            <div className="space-y-2">
              <Label className="text-white">Is the control operating effectively?</Label>
              <Select
                value={data.rollforwardOperatingEffective || "Not Yet Concluded"}
                onValueChange={handleRollforwardOperatingEffectiveChange}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-black/90 border-white/10">
                  <SelectItem value="Yes">Yes</SelectItem>
                  <SelectItem value="No">No</SelectItem>
                  <SelectItem value="Not Yet Concluded">Not Yet Concluded</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

